import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTemplates, deleteTemplate, duplicateTemplate } from '../../services/overlayApi';
import { designsApi } from '../../services/designsApi';
import TemplateCard from '../../components/TemplateCard/TemplateCard';
import CreativeCard from '../../components/CreativeCard/CreativeCard';
import TemplateSelectionModal from '../../components/TemplateSelectionModal/TemplateSelectionModal';
import ManageTemplateModal from '../../components/ManageTemplateModal/ManageTemplateModal';
import { Loader2, Plus, Palette, Layers, FilePlus } from 'lucide-react';
import PageHeader from '../../components/PageHeader/PageHeader';

import { useNavigate } from 'react-router-dom';

const TemplateGallery = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isManageTemplateModalOpen, setIsManageTemplateModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();


  // Fetch Templates
  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => getTemplates(),
  });

  // Fetch Designs (formerly Creatives)
  const { data: designs = [], isLoading: designsLoading } = useQuery({
    queryKey: ['designs'],
    queryFn: () => designsApi.list(),
  });

  // Adaptation: API returns array directly based on user's spec ("Returns an array of design objects")
  // but let's be safe and separate if it's wrapped. 
  // User spec: Returns an array of design objects.
  const creatives = Array.isArray(designs) ? designs : (designs.data || []);
  const isLoading = templatesLoading || designsLoading;

  // -- Template Mutations --
  const deleteTemplateMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(['templates']);
    },
    onError: (error) => {
      alert(`Failed to delete template: ${error.message}`);
    }
  });

  const duplicateTemplateMutation = useMutation({
    mutationFn: duplicateTemplate,
    onSuccess: (data) => {
      window.location.href = `/template/builder/${data.id}`;
    },
    onError: (error) => {
      alert(`Failed to create design from template: ${error.message}`);
    }
  });

  // -- Design Mutations --
  const deleteDesignMutation = useMutation({
    mutationFn: (id) => designsApi.delete(id),
    onSuccess: () => {
      console.log("Delete success, invalidating cache");
      queryClient.invalidateQueries(['designs']);
    },
    onError: (error) => {
      console.error("Delete error:", error);
      alert(`Failed to delete design: ${error.message}`);
    }
  });
  
  const duplicateDesignMutation = useMutation({
      mutationFn: async (id) => {
          // Manual duplication: Get -> Create
          const design = await designsApi.get(id);
          const newDesignData = {
              name: `${design.name} (Copy)`,
              canvas_data: design.canvas_data || design.polotno_data, // Handle legacy field if needed
              product_ids: design.product_ids || [],
              rules: design.rules || {}
          };
          return designsApi.create(newDesignData);
      },
      onSuccess: () => {
          queryClient.invalidateQueries(['designs']);
      },
      onError: (error) => {
          alert(`Failed to duplicate design: ${error.message}`);
      }
  });


  // Handlers
  const handleTemplateDelete = (id) => {
    deleteTemplateMutation.mutate(id);
  };

  const handleTemplateDuplicate = (template) => {
    duplicateTemplateMutation.mutate(template.id);
  };
  
  const handleCreativeDelete = (id) => {
      deleteDesignMutation.mutate(id);
  };

  const handleCreativeDuplicate = (creative) => {
      duplicateDesignMutation.mutate(creative.id);
  };

  const handleCreativeClick = (creative) => {
      navigate(`/creative/builder/${creative.id}`);
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#8E0DFF]" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <PageHeader
        title="Designs"
        description="Manage your creatives and templates"
      >
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#8E0DFF] to-[#2F86DC] text-white font-medium rounded-xl hover:shadow-lg hover:shadow-[#8E0DFF]/25 transition-all"
        >
          <Plus className="w-5 h-5" />
          Create
        </button>
      </PageHeader>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-12">
        
        {/* Section 1: My Designs (Creatives) */}
        <section>

            
            {creatives.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {creatives.map((creative) => (
                        <CreativeCard
                            key={creative.id}
                            creative={creative}
                            onDelete={handleCreativeDelete}
                            onDuplicate={handleCreativeDuplicate}
                            onClick={handleCreativeClick}
                        />
                    ))}
                </div>
            ) : (
                <div className="bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 p-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white mb-4 shadow-sm">
                        <Layers className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        No creatives yet
                    </h3>
                    <p className="text-sm text-gray-500 mb-6">
                        Start by creating a new creative using a template
                    </p>
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Create New
                    </button>
                </div>
            )}
        </section>

        {/* Section 2: Template Gallery */}
        <section>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    Template Gallery
                </h2>
                <button
                    onClick={() => setIsManageTemplateModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Add Template
                </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {templates?.templates?.slice(0, 8).map((template) => (
                    <TemplateCard
                        key={`gallery-${template.id}`}
                        template={template}
                        previewImage={template.preview_url || template.thumbnail}
                        variant="simple"
                        onClick={() => handleTemplateDuplicate(template)}
                        onDelete={handleTemplateDelete} 
                    />
                ))}
            </div>
        </section>
      </div>

      {/* Template Selection Modal */}
      <TemplateSelectionModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />

      {/* Manage Template Modal */}
      <ManageTemplateModal 
        isOpen={isManageTemplateModalOpen} 
        onClose={() => setIsManageTemplateModalOpen(false)} 
      />
    </div>
  );
};

export default TemplateGallery;
